// Tactics Board draft persistence (row 107 / Wave 1 #9)
// Auto-save the in-progress tactic to localStorage so users can leave/return
// without losing their work, and restore on next open.

const DRAFT_PREFIX = 'tactic_draft_';
const DRAFT_TTL_MS = 7 * 24 * 60 * 60 * 1000; // 7 days
const MAX_RETRY = 1;

const _telemetry = (event, payload) => {
  if (typeof process !== 'undefined' && process.env && process.env.NODE_ENV === 'production') return;
  try { console.log('[telemetry]', event, payload); } catch {}
};

export function getDraftPrefix() { return DRAFT_PREFIX; }

function _key(userId, tacticId) {
  return `${DRAFT_PREFIX}${userId || 'anon'}_${tacticId || 'new'}`;
}

function _allDraftKeys() {
  const keys = [];
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith(DRAFT_PREFIX)) keys.push(k);
  }
  return keys;
}

// Fast deterministic hash for scene dirty-check. djb2.
export function hashScene(scene) {
  if (!scene) return '0';
  const s = JSON.stringify(scene);
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return String(h);
}

export function pruneExpiredDrafts() {
  try {
    const now = Date.now();
    _allDraftKeys().forEach(k => {
      try {
        const raw = localStorage.getItem(k);
        if (!raw) return;
        const entry = JSON.parse(raw);
        if (!entry?.savedAt || now - entry.savedAt > DRAFT_TTL_MS) {
          localStorage.removeItem(k);
        }
      } catch {
        // corrupt entry; remove
        localStorage.removeItem(k);
      }
    });
  } catch {
    // localStorage unavailable; ignore
  }
}

function _writeWithQuotaGuard(key, value, attempt = 0) {
  try {
    localStorage.setItem(key, value);
    if (attempt > 0) {
      // We hit quota and recovered — surface telemetry so ops sees the rescue path.
      _telemetry('tactics_draft_quota_exceeded', { key, recovered: true, attempt });
    }
    return true;
  } catch (err) {
    const isQuota = err && (err.name === 'QuotaExceededError' || err.code === 22 || err.code === 1014);
    if (!isQuota || attempt >= MAX_RETRY) {
      _telemetry('tactics_draft_quota_exceeded', { key, recovered: false, error: err && err.name });
      return false;
    }
    // Drop oldest draft and retry once
    const candidates = _allDraftKeys()
      .filter(k => k !== key)
      .map(k => {
        try {
          const e = JSON.parse(localStorage.getItem(k) || '{}');
          return { k, savedAt: e.savedAt || 0 };
        } catch { return { k, savedAt: 0 }; }
      })
      .sort((a, b) => a.savedAt - b.savedAt);
    if (candidates.length === 0) {
      _telemetry('tactics_draft_quota_exceeded', { key, recovered: false, reason: 'no-other-drafts' });
      return false;
    }
    localStorage.removeItem(candidates[0].k);
    return _writeWithQuotaGuard(key, value, attempt + 1);
  }
}

export function saveDraft(userId, tacticId, scene, meta = {}) {
  if (!scene) return false;
  const key = _key(userId, tacticId);
  const entry = {
    savedAt: Date.now(),
    tacticId: tacticId || 'new',
    scene,
    meta,
  };
  return _writeWithQuotaGuard(key, JSON.stringify(entry));
}

export function loadDraft(userId, tacticId) {
  try {
    const raw = localStorage.getItem(_key(userId, tacticId));
    if (!raw) return null;
    const entry = JSON.parse(raw);
    if (!entry?.savedAt) return null;
    if (Date.now() - entry.savedAt > DRAFT_TTL_MS) {
      localStorage.removeItem(_key(userId, tacticId));
      return null;
    }
    return entry;
  } catch {
    return null;
  }
}

// Returns the most recently saved draft for this user (any tactic), or null.
export function loadLatestDraftForUser(userId) {
  try {
    const prefix = `${DRAFT_PREFIX}${userId || 'anon'}_`;
    const candidates = _allDraftKeys().filter(k => k.startsWith(prefix));
    let best = null;
    for (const k of candidates) {
      try {
        const entry = JSON.parse(localStorage.getItem(k) || '{}');
        if (!entry?.savedAt) continue;
        if (Date.now() - entry.savedAt > DRAFT_TTL_MS) {
          localStorage.removeItem(k);
          continue;
        }
        if (!best || entry.savedAt > best.savedAt) best = entry;
      } catch {
        localStorage.removeItem(k);
      }
    }
    return best;
  } catch {
    return null;
  }
}

export function deleteDraft(userId, tacticId) {
  try { localStorage.removeItem(_key(userId, tacticId)); } catch {}
}

export function clearAllDraftsForUser(userId) {
  try {
    const prefix = `${DRAFT_PREFIX}${userId || 'anon'}_`;
    _allDraftKeys().filter(k => k.startsWith(prefix)).forEach(k => localStorage.removeItem(k));
  } catch {}
}

// Clear ALL drafts (used by 401 interceptor when we don't know the user id).
export function clearAllDrafts() {
  try {
    _allDraftKeys().forEach(k => localStorage.removeItem(k));
  } catch {}
}
